import React, { useState, useRef, useEffect } from 'react';
import TabContentSpinner from "~/ui-apps/design-system/atoms/TabContentSpinner";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faCopy, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import EvaluationEmptyState from './EvaluationEmptyState';
import { EvaluationChecklistIdeasOffcanvas } from "~/ui-apps/design-system/organisms/offcanvas/EvaluationChecklistIdeasOffcanvas";
import { FiltersOffcanvas } from "~/ui-apps/design-system/organisms/offcanvas/FiltersOffcanvas";
import type { FilterItem } from './EvaluationInputBox';
import NewFilterCriteriaCard from './NewFilterCriteriaCard';
import MatchingCompaniesList from './MatchingCompaniesList';
import type { MatchingCompany } from './MatchingCompanyRow';
import mockFilterListItem from '../filterData/screener_filters.json';
import axios from 'axios';
import MainAuthModal from "~/ui-apps/design-system/organisms/auth-modals/App";
import Button from '../design-system/atoms/Button';
import { captureException } from "~/utilities/sentry.utility";

// Update the filter interface to match the canvas format
interface Filter {
  id?: number;
  name: string;
  key: string;
  glossary_key?: string;
  short_description: string;
  query: string;
  user_id?: string | null;
}

interface FilterData {
  query: string;
  threshold?: string;
  description?: string;
  glossary_key?: string;
  name?: string;
  key?: string;
  short_description?: string;
  /** Human-readable clause e.g. "Market Cap >= 1T" when `query` is canonical API form */
  displayLabel?: string;
}

function canonicalChecklistQuery(item: FilterData): string {
  const t = item.threshold?.trim();
  if (t) return t;
  return (item.query || '').trim();
}

function checklistDisplayName(item: FilterData, canonicalQuery: string): string {
  if (item.displayLabel?.trim()) return item.displayLabel.trim();
  if (item.name?.trim()) return item.name.trim();
  const parts = canonicalQuery.trim().split(/\s+/);
  if (parts.length >= 3) {
    return `${parts[0].replace(/_/g, ' ')} ${parts[1]} ${parts.slice(2).join(' ')}`;
  }
  return canonicalQuery || (item.query || '').trim();
}

export interface EvaluationCriteriaAppProps {
  user?: any;
  isAuthenticated: boolean;
  displayPricingModal: boolean;
  isBeta: boolean;
}

const App: React.FC<EvaluationCriteriaAppProps> = ({
  user = null,
  isAuthenticated = false,
  displayPricingModal = false,
  isBeta = false,
}) => {

  const [evaluationChecklist, setEvaluationChecklist] = useState<Filter[]>([]);
  // Add new states for inline creation
  const [isAdding, setIsAdding] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const addFilterScrollTargetRef = useRef<HTMLDivElement>(null);
  const panelHistoryPushedRef = useRef(false);
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
  const [isFilterOffcanvasOpen, setIsFilterOffcanvasOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [activeEducationElementId, setActiveEducationElementId] = useState<string | null>(null);
  const [companyLength, setCompanyLength] = useState(0);
  const [isCompanyCountLoading, setIsCompanyCountLoading] = useState(false);
  const [screenerUrl, setScreenerUrl] = useState<string[]>([]);
  const [matchingCompanies, setMatchingCompanies] = useState<MatchingCompany[]>([]);
  const [companiesResetKey, setCompaniesResetKey] = useState(0);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isDisplayPricingModal, setIsDisplayPricingModal] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchChecklist();
    }
    if (!isAuthenticated) {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const fetchChecklist = () => {
    if (!isLoading) {
      setIsCompanyCountLoading(true);
    }
    axios.get(`/api/users/fetch-checklists/${user?.id}`)
      .then(response => {
        setEvaluationChecklist(response.data.data.userCheckList);
        setCompanyLength(response.data.data.screener || 0);
        setScreenerUrl(response.data.data.screenerUrl);
        setMatchingCompanies(response.data.data.companies || []);
        setCompaniesResetKey(k => k + 1);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching checklist:', error);
        captureException(error);
      })
      .finally(() => {
        setIsCompanyCountLoading(false);
      });
  }

  useEffect(() => {
    const selectedElement = document.getElementById(`suggestion-${selectedIndex}`);
    if (selectedElement && suggestionsRef.current) {
      selectedElement.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (!isAdding) return;

    let alive = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!alive) return;
        addFilterScrollTargetRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      });
    });

    return () => {
      alive = false;
    };
  }, [isAdding]);

  // Mobile device detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobileDevice(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const handleRemoveFilter = (id: number | undefined) => {
    setEvaluationChecklist(prev => prev.filter(filter => filter.id !== id));
    axios.post('/api/users/delete/checklist', { id, user_id: user?.id })
      .then(response => {
        if (response.data.success) {
          fetchChecklist();
        }
      })
      .catch(error => {
        console.error('Error deleting checklist:', error);
        captureException(error);
      });
  };

  const handleFilterSelect = (filter: Filter) => {
    const filters = [filter];
    setEvaluationChecklist(prev => [...prev, ...filters]);
    setIsTemplatesOpen(false);
  };

  const handleTemplateSelection = (filters: Filter[]) => {
    const uniqueFilters = filters.filter(
      (item) => !screenerUrl.includes(item.query)
    );

    if (isAuthenticated) {
      setEvaluationChecklist(prev => [...prev, ...uniqueFilters]);
      axios.post('/api/users/add-checklist', {
        dataObj: uniqueFilters,
        user_id: user?.id,
      })
        .then((response) => {
          if (response.data.success) {
            fetchChecklist();
            setIsTemplatesOpen(false);
          }
        })
        .catch((error) => {
          console.error('Error:', error.message);
          captureException(error);
        });
      setIsTemplatesOpen(false);
    } else {
      setIsTemplatesOpen(false);
      setIsSignInModalOpen(true);
    }
  };

  const onAddFilterClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (isAuthenticated) {
      setIsAdding(true);
    } else {
      setIsAdding(false);
      setIsSignInModalOpen(true);
    }

  }

  const onShowPanelClick = (panelName: any, e: any) => {
    e.stopPropagation();
    setActiveEducationElementId(panelName);
    setSelectedFilter(panelName);
    setIsFilterOffcanvasOpen(true);
    // Push a state to history when panel opens so back button can close it
    window.history.pushState({ panelOpen: true }, '');
    panelHistoryPushedRef.current = true;
  }

  const handleClosePanel = () => {
    setIsFilterOffcanvasOpen(false);
    setActiveEducationElementId(null);
    setSelectedFilter(null);
    // If we pushed a history state, replace it to clean up without triggering navigation
    if (panelHistoryPushedRef.current) {
      window.history.replaceState(null, '');
      panelHistoryPushedRef.current = false;
    }
  }

  // Handle browser back button to close panel
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isFilterOffcanvasOpen) {
        setIsFilterOffcanvasOpen(false);
        setActiveEducationElementId(null);
        setSelectedFilter(null);
        panelHistoryPushedRef.current = false;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isFilterOffcanvasOpen]);

  const onAddChecklist = (data: any) => {
    setEvaluationChecklist(prev => [...prev, data]);
    setIsAdding(false);
  }

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  };

  const onQuerySubmit = (data: FilterData | FilterData[]) => {

    if (isAuthenticated) {
      const currentChecklist = screenerUrl;
      const dataArray = Array.isArray(data) ? data : [data];

      const hasDuplicate = dataArray.some((item) =>
        currentChecklist.includes(canonicalChecklistQuery(item))
      );

      if (hasDuplicate) {
        showToastMessage('A filter with this criteria already exists');
        setIsDuplicate(true);
        setIsTemplatesOpen(false);
        return;
      }

      setIsAdding(false);
      const first = dataArray[0];
      const canonicalQuery = canonicalChecklistQuery(first);

      const newFilter: Filter = {
        query: canonicalQuery,
        name: checklistDisplayName(first, canonicalQuery),
        key: first.key || canonicalQuery,
        short_description: first.short_description || '',
        user_id: user?.id,
        glossary_key: first.glossary_key
      };

      setEvaluationChecklist(prev => [...prev, newFilter]);
      axios.post('/api/users/add-checklist', {
        dataObj: [newFilter],
        user_id: user?.id,
      })
        .then((response) => {

          if (response.data.success) {
            fetchChecklist();
            setIsAdding(false);
            setIsTemplatesOpen(false);
          }
        })
        .catch((error) => {
          console.error('Error:', error.message);
          captureException(error);
        });
    } else {
      setIsAdding(false);
      setIsSignInModalOpen(true);
      setIsTemplatesOpen(false);
    }
  };

  const newFilterCriteriaInputProps = {
    onQuerySubmit,
    onAddChecklist,
    filterList: mockFilterListItem as FilterItem[],
    isHomePage: false,
    handleFilterDataApi: () => {},
    onScreenerQueryAppend: () => {},
    isDesktop: true,
    isActive: true,
    isInputFocused: false,
    userId: user?.id || '',
    isAuthenticated,
    isDuplicate,
    setIsDuplicate,
    onShowPanelClick,
  };

  return (
    <>
      {isLoading ? (
        <TabContentSpinner />
      ) : (
        <div
          className={`relative mx-auto min-h-[700px] max-w-3xl px-1 pt-10 md:px-8 md:pt-14 ${
            evaluationChecklist.length > 0 ? 'pb-16 md:pb-20' : 'pb-20'
          }`}
        >
          <div
            className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[min(420px,50vh)] bg-[radial-gradient(ellipse_90%_60%_at_50%_-10%,rgba(16,185,129,0.14),transparent_55%)]"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-x-0 top-24 -z-10 mx-auto h-px max-w-lg bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent"
            aria-hidden
          />

          {evaluationChecklist.length === 0 && !isAdding && (
            <EvaluationEmptyState
              isMobileDevice={isMobileDevice}
              onBrowseTemplates={() => setIsTemplatesOpen(true)}
              onCreateYourOwn={onAddFilterClick}
            />
          )}
          {isAdding && evaluationChecklist.length === 0 && (
            <div className="mx-auto mt-12 flex w-full max-w-xl flex-col md:mt-20">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="relative flex shrink-0 items-center justify-center self-stretch">
                  <span className="relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-600 to-teal-700 text-xs font-bold tabular-nums text-white shadow-md ring-[3px] ring-emerald-100 md:h-9 md:w-9 md:text-sm">
                    1
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <NewFilterCriteriaCard
                    cardRef={addFilterScrollTargetRef}
                    onCancel={() => setIsAdding(false)}
                    {...newFilterCriteriaInputProps}
                  />
                </div>
              </div>
              {showToast && (
                <div className="mt-3 rounded-lg border border-red-200/80 bg-red-50/90 px-3 py-2 text-center sm:text-left">
                  <span className="text-sm font-medium text-red-800">{toastMessage}</span>
                </div>
              )}
            </div>
          )}

          {evaluationChecklist.length > 0 && (
            <>
              <div className="mx-auto mt-10 max-w-3xl md:mt-12">
                <p className="mb-7 flex flex-wrap items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700/80 md:mb-4 md:text-xs">
                  <span>Your criteria has</span>
                  <span className="inline-flex min-w-[1.75rem] items-center justify-center rounded-full border border-emerald-700 bg-emerald-700 px-2 py-0.5 normal-case tracking-normal tabular-nums text-xs font-bold text-white md:text-sm">
                    {evaluationChecklist.length}
                  </span>
                  <span className="font-bold text-emerald-700/80">
                    {evaluationChecklist.length === 1 ? 'filter' : 'filters'}
                  </span>
                </p>

                <div className="rounded-2xl p-0 md:border md:border-slate-200/70 md:bg-white md:p-5 md:shadow-sm md:shadow-slate-900/[0.03] md:ring-1 md:ring-white/80">
                  <div className="grid grid-cols-2 gap-x-1.5 gap-y-2.5 md:gap-x-3">
                    {evaluationChecklist.map((filter, index) => (
                      <div
                        key={filter.id ?? index}
                        className="group inline-flex w-full min-w-0 items-center justify-between bg-gradient-to-r from-emerald-700 to-teal-700 px-1.5 py-1 text-white rounded-lg md:py-1.5"
                      >
                        <button
                          type="button"
                          onClick={(e) => onShowPanelClick(filter.glossary_key, e)}
                          title={filter.short_description || filter.name}
                          aria-label={`View details for ${filter.name}`}
                          className="flex min-w-0 flex-1 items-start gap-0.5 text-left text-[10px] font-semibold leading-snug tracking-tight text-white focus-visible:outline-none md:items-center md:gap-1.5 md:text-[13px]"
                        >
                          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-0.5">
                            <span className="break-words">{filter.name}</span>
                            <span
                              className="hidden shrink-0 items-center text-white/80 group-hover:inline-flex"
                              aria-hidden
                            >
                              <FontAwesomeIcon
                                icon={faCircleInfo}
                                className="text-sm leading-none md:text-base"
                              />
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveFilter(filter.id)}
                          className="ml-0.5 mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full text-emerald-700/60 transition-colors hover:bg-red-100 hover:text-red-600 md:ml-1 md:mt-0 md:h-5 md:w-5"
                          aria-label={`Remove ${filter.name}`}
                        >
                          <FontAwesomeIcon
                            icon={faXmark}
                            className="text-[10px] md:text-[11px] text-red-400"
                            style={{ stroke: 'currentColor', strokeWidth: 50 }}
                            aria-hidden
                          />
                        </button>
                      </div>
                    ))}
                  </div>

                  {isAdding && (
                    <div className="mt-4 md:mt-5">
                      <NewFilterCriteriaCard
                        compact
                        cardRef={addFilterScrollTargetRef}
                        onCancel={() => setIsAdding(false)}
                        {...newFilterCriteriaInputProps}
                      />
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200/70 pt-4 md:mt-5">
                    <Button
                      type="outline"
                      size="xs"
                      onClick={() => setIsTemplatesOpen(true)}
                      iconPosition="left"
                      icon={<FontAwesomeIcon icon={faCopy} className="h-4 w-4" />}
                    >
                      Templates
                    </Button>
                    {!isAdding ? (
                      <button
                        type="button"
                        onClick={onAddFilterClick}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg !px-7 border-[1.5px] border-dashed border-emerald-700 bg-white py-[5px] text-xs font-semibold text-emerald-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 md:text-[13px]"
                      >
                        <FontAwesomeIcon icon={faPlus} className="text-[10px]" aria-hidden />
                        Add criteria
                      </button>
                    ) : null}
                  </div>
                </div>

                {showToast && (
                  <div className="mt-3 rounded-lg border border-red-200/80 bg-red-50/90 px-3 py-2">
                    <span className="text-sm font-medium text-red-800">{toastMessage}</span>
                  </div>
                )}
              </div>

              <MatchingCompaniesList
                initialCompanies={matchingCompanies}
                total={companyLength}
                screenerUrl={screenerUrl}
                resetKey={companiesResetKey}
                isInitialLoading={isCompanyCountLoading}
              />
            </>
          )}
        </div>
      )}

      <EvaluationChecklistIdeasOffcanvas
        isOpen={isTemplatesOpen}
        onClose={() => setIsTemplatesOpen(false)}
        onTemplateSelect={handleTemplateSelection}
        onFilterSelect={handleFilterSelect}
        onQuerySubmit={onQuerySubmit}
        isAuthenticated={isAuthenticated}
      />

      <FiltersOffcanvas
        isOpen={isFilterOffcanvasOpen}
        onClose={handleClosePanel}
        selectedFilter={selectedFilter}
        onExampleClick={() => { }}
        activeEducationElementId={activeEducationElementId}
        filterButtonPlaceholder="Add to Checklist"
        isScreenerComp={false}
        onQuerySubmit={onQuerySubmit}
      />

      <MainAuthModal
        isOpen={isSignInModalOpen}
        onClose={() => setIsSignInModalOpen(false)}
        query={''}
      />
    </>
  );
};

export default App;